/**
 * Utility functions for formatting salary amounts
 */

/**
 * Format a salary amount with proper k/M suffixes
 * @param amount - The salary amount in base currency units
 * @returns Formatted string (e.g., "75k", "1.5M", "800")
 */
export const formatSalaryNumber = (amount: number): string => {
  if (amount >= 1000000) {
    // For millions (1M+)
    const millions = amount / 1000000;
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
  } else if (amount >= 1000) {
    // For thousands (1K+)
    const thousands = amount / 1000;
    return thousands % 1 === 0 ? `${thousands}k` : `${thousands.toFixed(0)}k`;
  } else {
    // For amounts less than 1000
    return amount.toLocaleString();
  }
};

/**
 * Format a salary range with currency
 * @param from - Minimum salary amount
 * @param to - Maximum salary amount (optional)
 * @param currency - Currency code (e.g., 'USD', 'EUR')
 * @returns Formatted salary range string
 */
export const formatSalaryRange = (
  from: number | null,
  to: number | null,
  currency: string
): string | null => {
  if (!from && !to) return null;

  const currencyUpper = currency.toUpperCase();
  const fromFormatted = from ? formatSalaryNumber(from) : '';
  const toFormatted = to ? formatSalaryNumber(to) : '';

  if (fromFormatted && toFormatted) {
    return `${fromFormatted} - ${toFormatted} ${currencyUpper}`;
  }
  if (fromFormatted) {
    return `From ${fromFormatted} ${currencyUpper}`;
  }
  if (toFormatted) {
    return `Up to ${toFormatted} ${currencyUpper}`;
  }

  return null;
};

/**
 * Format salary with period (e.g., "75k - 100k EUR / Annual")
 * @param from - Minimum salary amount
 * @param to - Maximum salary amount (optional)
 * @param currency - Currency code
 * @param period - Compensation period (e.g., 'annual', 'monthly')
 * @returns Formatted salary string with period
 */
export const formatSalaryWithPeriod = (
  from: number | null,
  to: number | null,
  currency: string,
  period?: string
): string | null => {
  const salaryRange = formatSalaryRange(from, to, currency);
  if (!salaryRange) return null;

  const periodFormatted =
    period && period.toLowerCase() !== 'unspecified'
      ? ` / ${period.charAt(0).toUpperCase() + period.slice(1)}`
      : '';

  return `${salaryRange}${periodFormatted}`.trim();
};
